import { useEffect, useState } from "react";
import { PageHeader, EmptyState } from "@/components/Layout";
import { Button, ErrorNote, Field, SidePanel, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage, type PropFirmPatch } from "@/lib/api";
import { ArchivedFirms } from "@/components/archived-firms";
import { OrphanServers, OwnerServers, OwnerToggle, serversSummary, useServersByOwner } from "@/components/owner-servers";
import { usePropFirms, useRefreshDirectory } from "@/lib/queries";
import { decimalInput, fractionToPct, intInput, pctIssue, pctToFraction } from "@/lib/units";
import type { DdBasis } from "@match/shared";
import type { BrokerServer, Preset, PresetInput, PropFirm } from "@/lib/types";

/*
 * Prop firm e i loro programmi.
 *
 * I preset sono di una FIRM, non del tenant: la stessa firm vende più programmi
 * (50k una fase, 100k due fasi) e sono quelli a decidere taglia, fee, max DD e
 * target. Senza un preset non si apre nessun ciclo, ed è il motivo per cui
 * questa pagina esiste: le rotte c'erano già, l'interfaccia no, e la dashboard
 * si fermava prima del primo ciclo.
 *
 * Le percentuali si scrivono come PERCENTUALI e viaggiano come FRAZIONI: la
 * conversione sta in `lib/units.ts` e succede solo lì. È la trappola del foglio
 * di calcolo originale, dove due schede usavano unità diverse e funzionava per
 * caso.
 */

/** Etichette delle basi di calcolo del drawdown. */
const DD_BASIS: Record<DdBasis, string> = {
  STATIC_INITIAL: "Statico sulla taglia iniziale",
  STATIC_BALANCE: "Statico sul saldo",
  EOD_BALANCE: "Saldo di fine giornata",
  TRAILING_HIGH_WATER: "Trailing sul massimo",
  TRAILING_LOCKED_AT_INITIAL: "Trailing, bloccato alla taglia",
};

const money = (d: string) => Number(d).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const pct = (d: string | null) => (d === null ? "—" : `${fractionToPct(d)}%`);

export function PropFirms() {
  const canWrite = useCanWrite();
  const firms = usePropFirms();
  const servers = useServersByOwner("PROP", firms.data);
  const [firmPanel, setFirmPanel] = useState(false);
  const [presetPanel, setPresetPanel] = useState<{ firm: PropFirm; preset: Preset | null } | null>(null);

  return (
    <>
      <PageHeader
        title="Prop firm"
        subtitle="Le firm e i loro programmi: taglia, fee, drawdown e target da cui nasce ogni ciclo"
        actions={canWrite && <Button variant="primary" onClick={() => setFirmPanel(true)}>Nuova prop firm</Button>}
      />

      {firms.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : firms.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(firms.error)}</ErrorNote>
        </div>
      ) : !firms.data?.length ? (
        <EmptyState
          title="Nessuna prop firm"
          hint="Aggiungi la firm e almeno un suo programma: senza un preset non si apre nessun ciclo, perché è da lì che vengono taglia, fee, max drawdown e target."
          action={canWrite && <Button variant="primary" onClick={() => setFirmPanel(true)}>Nuova prop firm</Button>}
        />
      ) : (
        <div className="space-y-4 px-6 py-4">
          {firms.data.map((f) => (
            <FirmCard
              key={f.id}
              firm={f}
              servers={servers.ready ? servers.byOwner.get(f.id) ?? [] : null}
              serversError={servers.error}
              canWrite={canWrite}
              onPreset={(preset) => setPresetPanel({ firm: f, preset })}
            />
          ))}
          {servers.ready && <OrphanServers kind="PROP" servers={servers.orphans} />}
        </div>
      )}

      <ArchivedFirms kind="PROP" />

      {firmPanel && <FirmPanel onClose={() => setFirmPanel(false)} />}
      {presetPanel && <PresetPanel firm={presetPanel.firm} preset={presetPanel.preset} onClose={() => setPresetPanel(null)} />}
    </>
  );
}

/**
 * ARCHIVIA una firm o un suo programma. **Non si cancellano mai.**
 *
 * Un preset usato da un ciclo — anche chiuso — serve ancora a rileggerne i
 * numeri: taglia, fee e max DD sono quelli con cui quel ciclo è stato
 * calcolato. Qui non c'è un «Elimina» accanto, e non è una dimenticanza: per
 * queste due entità la cancellazione non esiste, quindi non ci sono due metà da
 * mostrare insieme.
 *
 * La colonna `isArchived` c'era da sempre e **non la scriveva nessuno**: non
 * serviva una rotta nuova, serviva quella che usa la colonna che c'è.
 */
export function ArchiveCatalogo({ kind, id, label, count }: { kind: "firm" | "broker" | "preset"; id: string; label: string; count?: number }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const archivia = async () => {
    setBusy(true);
    setError(null);
    try {
      // Firm e broker stanno nella stessa tabella: la rotta è la stessa.
      await (kind === "preset" ? api.propFirms.archivePreset(id) : api.propFirms.archive(id));
      setConfirm(false);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size="sm" variant="danger" disabled={busy} onClick={() => void archivia()}>
            {busy ? "Archivio…" : `Conferma: archivia ${label}`}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
          <span className="text-xs text-faint">
            {kind === "firm"
              ? "Sparisce dagli elenchi coi suoi programmi. I cicli che li usano continuano a leggerli."
              : kind === "broker"
                ? "Sparisce dall'elenco. I suoi server restano, e passano in «Server senza possessore»."
                : `Non sarà più scegliibile per una Fase nuova.${count ? ` I ${count} cicli che lo usano continuano a leggerlo.` : ""}`}
          </span>
        </>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
          Archivia
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}

function FirmCard({
  firm,
  servers,
  serversError,
  canWrite,
  onPreset,
}: {
  firm: PropFirm;
  /** `null` = server non ancora letti. */
  servers: BrokerServer[] | null;
  serversError: unknown;
  canWrite: boolean;
  onPreset: (p: Preset | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const programmi =
    firm.presets.length === 0
      ? "Nessun programma: senza preset non si apre un ciclo"
      : `${firm.presets.length} ${firm.presets.length === 1 ? "programma" : "programmi"}`;
  const summary = [programmi, servers ? serversSummary(servers) : null].filter(Boolean).join(" · ") + (firm.externalHedgingAllowed ? "" : " · copertura esterna non dichiarata consentita");

  return (
    <section className="card pair-side-prop">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <OwnerToggle open={open} onToggle={() => setOpen((o) => !o)} title={firm.name} summary={summary} />
        {canWrite && (
          <span className="inline-flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => onPreset(null)}>Nuovo programma</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Modifica
            </Button>
            <ArchiveCatalogo kind="firm" id={firm.id} label={firm.name} />
          </span>
        )}
      </header>

      {open && (
        <>
          {firm.presets.length === 0 ? (
            <p className="border-t border-line px-4 py-4 text-sm text-muted">
              Aggiungi il programma che userai: la taglia, la fee e le regole di drawdown sono i numeri da cui il motore
              calcola i lotti.
            </p>
          ) : (
            <div className="overflow-x-auto border-t border-line">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs text-faint">
                    <th className="px-4 py-2 font-medium">Programma</th>
                    <th className="py-2 pr-4 text-right font-medium">Taglia</th>
                    <th className="py-2 pr-4 text-right font-medium">Fee</th>
                    <th className="py-2 pr-4 text-right font-medium">Fasi</th>
                    <th className="py-2 pr-4 text-right font-medium">Max DD</th>
                    <th className="py-2 pr-4 text-right font-medium">DD giorno</th>
                    <th className="py-2 pr-4 text-right font-medium">Target 1</th>
                    <th className="py-2 pr-4 text-right font-medium">Target 2</th>
                    <th className="py-2 pr-4 text-right font-medium">Funded</th>
                    <th className="py-2 pr-4 font-medium">Base DD</th>
                    <th className="py-2 pr-4">
                      <span className="sr-only">Azioni</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {firm.presets.map((p) => (
                    <tr key={p.id} className="border-b border-line/60 last:border-0">
                      <td className="px-4 py-2 font-medium text-ink">{p.name}</td>
                      <td className="py-2 pr-4 text-right">{money(p.accountSize)}</td>
                      <td className="py-2 pr-4 text-right">{Number(p.challengeFee) > 0 ? money(p.challengeFee) : <span title="Fee non registrata">—</span>}</td>
                      <td className="py-2 pr-4 text-right">{p.phases}</td>
                      <td className="py-2 pr-4 text-right">{pct(p.maxDdPct)}</td>
                      <td className="py-2 pr-4 text-right">{pct(p.dailyDdPct)}</td>
                      <td className="py-2 pr-4 text-right">{pct(p.phase1TargetPct)}</td>
                      <td className="py-2 pr-4 text-right">{pct(p.phase2TargetPct)}</td>
                      <td className="py-2 pr-4 text-right">{pct(p.fundedTargetPct)}</td>
                      <td className="py-2 pr-4 text-xs text-muted">{DD_BASIS[p.maxDdBasis]}</td>
                      <td className="py-2 pr-4 text-right">
                        {canWrite && (
                          <span className="inline-flex flex-wrap items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => onPreset(p)}>
                              Modifica
                            </Button>
                            <ArchiveCatalogo kind="preset" id={p.id} label={p.name} />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <OwnerServers ownerId={firm.id} ownerName={firm.name} kind="PROP" servers={servers} error={serversError} />
        </>
      )}
      {editing && <FirmPanel kind="PROP" firm={firm} onClose={() => setEditing(false)} />}
    </section>
  );
}

/**
 * Una prop firm o un broker, nuovo o da correggere: stessa tabella, stesso
 * pannello. Il tipo lo decide la pagina da cui si apre e non si cambia dopo. La
 * regola sulla copertura esterna è di una prop firm, e per un broker non si
 * chiede. In modifica parte dai valori attuali e manda solo ciò che cambia.
 */
export function FirmPanel({ kind = "PROP", firm, onClose }: { kind?: "PROP" | "BROKER"; firm?: PropFirm; onClose: () => void }) {
  const isBroker = kind === "BROKER";
  const refresh = useRefreshDirectory();
  const [name, setName] = useState(firm?.name ?? "");
  const [site, setSite] = useState(firm?.websiteUrl ?? "");
  const [hedging, setHedging] = useState(firm?.externalHedgingAllowed ?? false);
  const [notes, setNotes] = useState(firm?.hedgingRuleNotes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (firm) {
        const body: PropFirmPatch = {
          ...(name.trim() !== firm.name ? { name: name.trim() } : {}),
          ...((site.trim() || null) !== firm.websiteUrl ? { websiteUrl: site.trim() || null } : {}),
          ...(!isBroker && hedging !== firm.externalHedgingAllowed ? { externalHedgingAllowed: hedging } : {}),
          ...(!isBroker && (notes.trim() || null) !== firm.hedgingRuleNotes ? { hedgingRuleNotes: notes.trim() || null } : {}),
        };
        if (Object.keys(body).length > 0) await api.propFirms.update(firm.id, body);
      } else {
        await api.propFirms.create({
          name: name.trim(),
          kind,
          websiteUrl: site.trim() || null,
          ...(isBroker ? {} : { externalHedgingAllowed: hedging, hedgingRuleNotes: notes.trim() || null }),
        });
      }
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title={firm ? `Modifica ${firm.name}` : isBroker ? "Nuovo broker" : "Nuova prop firm"}
      subtitle={
        firm
          ? isBroker
            ? "Il nome è unico fra prop firm e broker"
            : "Il nome è unico fra prop firm e broker; la regola sulla copertura si registra, non si assume"
          : isBroker
            ? "Il broker; i suoi server si aggiungono dentro di lui"
            : "La firm; i programmi si aggiungono dentro di lei"
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="firm-form" disabled={busy}>
            {busy ? "Salvataggio…" : firm ? "Salva" : "Aggiungi"}
          </Button>
        </>
      }
    >
      <form id="firm-form" onSubmit={submit} className="space-y-4">
        <Field label="Nome">{(id) => <input id={id} required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />}</Field>
        <Field label="Sito" hint="Facoltativo.">
          {(id) => <input id={id} type="url" value={site} placeholder="https://" onChange={(e) => setSite(e.target.value)} className={inputClass} />}
        </Field>
        {!isBroker && (
          <>
            <Field label="Copertura esterna consentita" hint="Quello che la firm dichiara sulle posizioni aperte altrove. Si registra, non si assume.">
              {(id) => (
                <div id={id} className="flex items-center gap-2">
                  <input type="checkbox" checked={hedging} onChange={(e) => setHedging(e.target.checked)} />
                  <span className="text-sm text-muted">La firm la consente</span>
                </div>
              )}
            </Field>
            <Field label="Note sulla regola">
              {(id) => <textarea id={id} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
            </Field>
          </>
        )}
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}


/**
 * Plausibilità delle percentuali, prima del salvataggio.
 *
 * Il backend rifiuta già una frazione fuori da (0, 1], quindi «6» al posto di
 * «0.06» non passa. Resta l'errore speculare, che nessun vincolo coglie: 0,06
 * scritto nel campo «%» diventa lo 0,06 per cento, cioè un max DD di 60 dollari
 * su un conto da 100 000. È un avviso, non un blocco: i programmi strani
 * esistono, e un gestionale che si rifiuta di salvare un numero vero è peggio
 * di uno che lo salva segnalandolo.
 */
function pctWarning(label: string, value: string, min: number, max: number): string | null {
  const n = Number(value.trim().replace(",", "."));
  if (!value.trim() || !Number.isFinite(n)) return null;
  if (n < min) return `${label} ${n}% è insolitamente basso: hai scritto una frazione in un campo che vuole la percentuale?`;
  if (n > max) return `${label} ${n}% è insolitamente alto: ricontrolla.`;
  return null;
}

function PresetPanel({ firm, preset, onClose }: { firm: PropFirm; preset: Preset | null; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const [name, setName] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [fee, setFee] = useState("");
  const [phases, setPhases] = useState("2");
  const [maxDd, setMaxDd] = useState("");
  const [dailyDd, setDailyDd] = useState("");
  const [maxDdBasis, setMaxDdBasis] = useState<DdBasis>("STATIC_INITIAL");
  const [dailyDdBasis, setDailyDdBasis] = useState<DdBasis>("EOD_BALANCE");
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [tFunded, setTFunded] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [split, setSplit] = useState("");
  const [refund, setRefund] = useState(false);
  const [minDays, setMinDays] = useState("");
  const [consistency, setConsistency] = useState("");
  const [inactivity, setInactivity] = useState("");
  const [news, setNews] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(preset?.name ?? "");
    setAccountSize(preset ? String(Number(preset.accountSize)) : "");
    setFee(preset && Number(preset.challengeFee) > 0 ? String(Number(preset.challengeFee)) : "");
    setPhases(preset ? String(preset.phases) : "2");
    setMaxDd(fractionToPct(preset?.maxDdPct));
    setDailyDd(fractionToPct(preset?.dailyDdPct));
    setMaxDdBasis(preset?.maxDdBasis ?? "STATIC_INITIAL");
    setDailyDdBasis(preset?.dailyDdBasis ?? "EOD_BALANCE");
    setT1(fractionToPct(preset?.phase1TargetPct));
    setT2(fractionToPct(preset?.phase2TargetPct));
    setTFunded(fractionToPct(preset?.fundedTargetPct));
    setMultiplier(preset ? String(Number(preset.defaultMultiplier)) : "");
    setSplit(fractionToPct(preset?.profitSplitPct));
    setRefund(preset?.feeRefundOnFirstPayout ?? false);
    setMinDays(preset?.minTradingDays === null || preset?.minTradingDays === undefined ? "" : String(preset.minTradingDays));
    setConsistency(fractionToPct(preset?.consistencyPct));
    setInactivity(preset?.inactivityDays === null || preset?.inactivityDays === undefined ? "" : String(preset.inactivityDays));
    setNews(preset?.newsTradingAllowed ?? true);
    setError(null);
  }, [preset]);

  /**
   * Testo non capito e oltre il 100%: bloccano il salvataggio, non lo segnalano
   * soltanto. Sopra il 100% è un errore di unità (un drawdown del mille per cento
   * non brucia mai il conto); un testo come «6%» o «abc» non deve svuotare il
   * campo in silenzio (`lib/units.ts`, `parsePct`).
   */
  const errori = [
    pctIssue("Max drawdown", maxDd),
    pctIssue("Drawdown giornaliero", dailyDd),
    pctIssue("Target fase 1", t1),
    pctIssue("Target fase 2", t2),
    pctIssue("Target funded", tFunded),
    pctIssue("Split del payout", split),
    pctIssue("Consistenza", consistency),
  ].filter((x): x is string => x !== null);

  const avvisi = [
    pctWarning("Il max drawdown", maxDd, 1, 50),
    pctWarning("Il drawdown giornaliero", dailyDd, 1, 50),
    pctWarning("Il target di fase 1", t1, 1, 50),
    pctWarning("Il target di fase 2", t2, 1, 50),
    pctWarning("Il target funded", tFunded, 1, 50),
    pctWarning("Lo split del payout", split, 10, 100),
  ].filter((x): x is string => x !== null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (errori.length) return setError(errori.join(" "));

    const size = decimalInput(accountSize);
    const dd = pctToFraction(maxDd);
    if (!size) return setError("La taglia del conto deve essere un numero, per esempio 50000.");
    if (!dd) return setError("Il max drawdown deve essere una percentuale, per esempio 10.");

    const body: PresetInput = {
      name: name.trim(),
      accountSize: size,
      // Fee vuota = non registrata, e vale ZERO: nel modello la fee entra nel
      // recover, quindi un numero inventato sposterebbe ogni proiezione.
      challengeFee: decimalInput(fee) ?? "0",
      phases: intInput(phases) ?? 2,
      maxDdPct: dd,
      dailyDdPct: pctToFraction(dailyDd),
      maxDdBasis,
      dailyDdBasis,
      phase1TargetPct: pctToFraction(t1),
      phase2TargetPct: pctToFraction(t2),
      fundedTargetPct: pctToFraction(tFunded),
      ...(decimalInput(multiplier) ? { defaultMultiplier: decimalInput(multiplier)! } : {}),
      profitSplitPct: pctToFraction(split),
      feeRefundOnFirstPayout: refund,
      minTradingDays: intInput(minDays),
      consistencyPct: pctToFraction(consistency),
      inactivityDays: intInput(inactivity),
      newsTradingAllowed: news,
    };

    setBusy(true);
    try {
      if (preset) await api.propFirms.updatePreset(preset.id, body);
      else await api.propFirms.createPreset(firm.id, body);
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const pctField = (label: string, value: string, set: (v: string) => void, hint?: string) => (
    <Field label={`${label} (%)`} hint={hint}>
      {(id) => <input id={id} inputMode="decimal" value={value} onChange={(e) => set(e.target.value)} className={inputClass} />}
    </Field>
  );

  const basisField = (label: string, value: DdBasis, set: (v: DdBasis) => void) => (
    <Field label={label}>
      {(id) => (
        <select id={id} value={value} onChange={(e) => set(e.target.value as DdBasis)} className={inputClass}>
          {(Object.keys(DD_BASIS) as DdBasis[]).map((b) => (
            <option key={b} value={b}>
              {DD_BASIS[b]}
            </option>
          ))}
        </select>
      )}
    </Field>
  );

  return (
    <SidePanel
      open
      onClose={onClose}
      title={preset ? `Modifica ${preset.name}` : "Nuovo programma"}
      subtitle={`${firm.name} · i numeri da cui nasce un ciclo`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="preset-form" disabled={busy || errori.length > 0}>
            {busy ? "Salvataggio…" : preset ? "Salva" : "Aggiungi programma"}
          </Button>
        </>
      }
    >
      <form id="preset-form" onSubmit={submit} className="space-y-4">
        <Field label="Nome del programma" hint="Come lo chiama la firm: «50k 1-Step», «100k Challenge».">
          {(id) => <input id={id} required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />}
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Taglia">
            {(id) => <input id={id} required inputMode="decimal" value={accountSize} placeholder="50000" onChange={(e) => setAccountSize(e.target.value)} className={inputClass} />}
          </Field>
          <Field label="Fee" hint="Vuota = non registrata.">
            {(id) => <input id={id} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} className={inputClass} />}
          </Field>
          <Field label="Fasi" hint="0 instant, 1 one-step, 2 two-step.">
            {(id) => (
              <select id={id} value={phases} onChange={(e) => setPhases(e.target.value)} className={inputClass}>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {pctField("Max drawdown", maxDd, setMaxDd, "Decide i lotti: è il budget del ciclo.")}
          {pctField("Drawdown giornaliero", dailyDd, setDailyDd, "Vuoto = la firm non lo impone.")}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {basisField("Base del max DD", maxDdBasis, setMaxDdBasis)}
          {basisField("Base del DD giornaliero", dailyDdBasis, setDailyDdBasis)}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {pctField("Target fase 1", t1, setT1)}
          {pctField("Target fase 2", t2, setT2)}
          {pctField("Target funded", tFunded, setTFunded, "Vuoto = il programma non lo dichiara.")}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Moltiplicatore suggerito" hint="Resta modificabile a ogni operazione.">
            {(id) => <input id={id} inputMode="decimal" value={multiplier} placeholder="0.21" onChange={(e) => setMultiplier(e.target.value)} className={inputClass} />}
          </Field>
        </div>

        {errori.length > 0 && (
          <div role="alert" className="space-y-1 rounded border border-loss/40 bg-loss/10 px-3 py-2 text-sm text-loss">
            {errori.map((e) => (
              <p key={e}>{e}</p>
            ))}
            <p className="text-xs">
              Qui le percentuali si scrivono come percentuali: 10 per il dieci per cento, non 0,10 e non 1000.
            </p>
          </div>
        )}

        {avvisi.length > 0 && (
          <div role="status" className="space-y-1 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            {avvisi.map((a) => (
              <p key={a}>{a}</p>
            ))}
            <p className="text-xs text-warn/80">Si salva lo stesso: è un controllo di plausibilità, non una regola.</p>
          </div>
        )}

        <div className="space-y-3 rounded border border-line px-3 py-3">
          <p className="text-xs text-faint">
            Regole della firm che il gestionale REGISTRA e che oggi nessun calcolo legge. Servono a ricordarsele, non a
            decidere: quando entreranno nei controlli, il dato sarà già qui.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {pctField("Split del payout", split, setSplit, "Vuoto o 0 = non dichiarato.")}
            <Field label="Giorni minimi">
              {(id) => <input id={id} inputMode="numeric" value={minDays} onChange={(e) => setMinDays(e.target.value.replace(/[^0-9]/g, ""))} className={inputClass} />}
            </Field>
            <Field label="Giorni di inattività">
              {(id) => <input id={id} inputMode="numeric" value={inactivity} onChange={(e) => setInactivity(e.target.value.replace(/[^0-9]/g, ""))} className={inputClass} />}
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {pctField("Consistenza", consistency, setConsistency, "Vuoto o 0 = non dichiarata.")}
            <Field label="Rimborso fee">
              {(id) => (
                <div id={id} className="flex items-center gap-2 pt-2">
                  <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} />
                  <span className="text-sm text-muted">Al primo payout</span>
                </div>
              )}
            </Field>
            <Field label="News trading">
              {(id) => (
                <div id={id} className="flex items-center gap-2 pt-2">
                  <input type="checkbox" checked={news} onChange={(e) => setNews(e.target.checked)} />
                  <span className="text-sm text-muted">Consentito</span>
                </div>
              )}
            </Field>
          </div>
        </div>

        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
